/**
 * Batch 18-K108 — CompanionInstallModal
 *
 * Shown when a technician tries to launch monitored training but the OptiBay
 * Training Companion extension is not detected on this browser/profile. It
 * explains the one-time install and lets the user re-check after installing.
 *
 * Popup-safety: the actual training launch (`onProceed`) is only ever invoked
 * from a real button click INSIDE this modal (after a successful re-check), so
 * the caller's `window.open` still runs inside a user gesture.
 *
 * No token is involved here — detection uses the tokenless PING/PONG.
 */

import { useCallback, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  pingCompanion,
  detectBrowserFamily,
  COMPANION_CHROME_WEBSTORE_URL,
  COMPANION_EDGE_ADDONS_URL,
  type BrowserFamily,
} from '@/lib/trainingCompanion/companionAvailability';

export interface CompanionInstallModalProps {
  open: boolean;
  onClose: () => void;
  /** Invoked (from a user click) once the Companion is detected. */
  onProceed: () => void;
  /** Product policy: allow launching without verified monitoring. */
  allowContinueWithout?: boolean;
  onContinueWithout?: () => void;
  /** Override for tests; defaults to UA detection. */
  browserFamily?: BrowserFamily;
}

export function CompanionInstallModal({
  open,
  onClose,
  onProceed,
  allowContinueWithout = false,
  onContinueWithout,
  browserFamily,
}: CompanionInstallModalProps) {
  const family = browserFamily ?? detectBrowserFamily();
  const [checking, setChecking] = useState(false);
  const [detected, setDetected] = useState(false);
  const [checkedOnce, setCheckedOnce] = useState(false);

  const handleCheckAgain = useCallback(async () => {
    setChecking(true);
    try {
      const { installed } = await pingCompanion();
      setDetected(installed);
      setCheckedOnce(true);
      // Do NOT auto-launch here — that would be outside the user gesture and
      // popup-blocked. We surface an "Open training" button instead.
       
      console.log(`[Companion] install re-check: ${installed ? 'installed' : 'not installed'}`);
    } finally {
      setChecking(false);
    }
  }, []);

  const openChromeStore = useCallback(() => {
    window.open(COMPANION_CHROME_WEBSTORE_URL, '_blank', 'noopener,noreferrer');
  }, []);

  const openEdgeStore = useCallback(() => {
    if (COMPANION_EDGE_ADDONS_URL) {
      window.open(COMPANION_EDGE_ADDONS_URL, '_blank', 'noopener,noreferrer');
    }
  }, []);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="companion-install-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Install OptiBay Training Companion</DialogTitle>
          <DialogDescription>
            This browser needs the OptiBay Training Companion extension so OptiBay can verify
            launched training sessions. Install it once, <strong>reload this OptiBay tab</strong>,
            then come back and continue.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {family === 'safari' ? (
            <div className="space-y-2 text-sm" data-testid="companion-safari-setup">
              <p className="font-medium text-foreground">
                Safari requires one-time website access for OptiBay Training Companion.
              </p>
              <ol
                className="ml-4 list-decimal space-y-1 text-muted-foreground"
                data-testid="companion-safari-checklist"
              >
                <li>Enable <strong>OptiBay Training Companion</strong> in Safari &rarr; Settings &rarr; Extensions</li>
                <li>Allow <code>app.optibayai.com</code></li>
                <li>Allow <code>youtube.com</code></li>
                <li>Allow <code>youtu.be</code></li>
                <li>Allow <code>m.youtube.com</code></li>
                <li>Return to OptiBay and try again</li>
              </ol>
              <p className="text-xs text-muted-foreground" data-testid="companion-safari-fallback-note">
                Training completion and quizzes still work without Companion evidence, but
                confidence will remain self-reported / not Companion verified.
              </p>
            </div>
          ) : family === 'edge' && !COMPANION_EDGE_ADDONS_URL ? (
            <>
              <Button
                data-testid="companion-install-chrome"
                className="w-full"
                onClick={openChromeStore}
              >
                Install for Chrome
              </Button>
              <p className="text-xs text-muted-foreground" data-testid="companion-edge-note">
                Microsoft Edge support is pending store review. Chrome is currently supported —
                please use Chrome for verified monitoring for now.
              </p>
            </>
          ) : family === 'edge' && COMPANION_EDGE_ADDONS_URL ? (
            <Button
              data-testid="companion-install-edge"
              className="w-full"
              onClick={openEdgeStore}
            >
              Install for Microsoft Edge
            </Button>
          ) : (
            <Button
              data-testid="companion-install-chrome"
              className="w-full"
              onClick={openChromeStore}
            >
              Install for Chrome
            </Button>
          )}

          {detected && (
            <div
              data-testid="companion-detected-banner"
              className="rounded-md bg-muted px-3 py-2 text-sm text-foreground"
            >
              Companion detected. You can open training now.
            </div>
          )}
          {checkedOnce && !detected && (
            <div
              data-testid="companion-not-detected-banner"
              className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground"
            >
              Still not detected. After installing, you must <strong>reload this OptiBay
              tab</strong> so the extension can connect — the newly installed Companion does not
              attach to tabs that were already open. Confirm it is installed and enabled at
              {' '}<code>chrome://extensions</code>, reload this page, then check again.
              <div className="mt-2">
                <Button
                  data-testid="companion-reload-tab"
                  variant="outline"
                  size="sm"
                  onClick={() => window.location.reload()}
                >
                  Reload this tab
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          {detected ? (
            <Button
              data-testid="companion-open-training"
              className="w-full"
              onClick={() => {
                onProceed();
                onClose();
              }}
            >
              Open training
            </Button>
          ) : (
            <Button
              data-testid="companion-check-again"
              variant="secondary"
              className="w-full"
              onClick={handleCheckAgain}
              disabled={checking}
            >
              {checking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Checking…
                </>
              ) : (
                "I installed it — check again"
              )}
            </Button>
          )}

          {allowContinueWithout && onContinueWithout && (
            <Button
              data-testid="companion-continue-without"
              variant="ghost"
              className="w-full text-xs"
              onClick={() => {
                onContinueWithout();
                onClose();
              }}
            >
              Continue without verified monitoring (completion may not be verified)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
